import { useTranslation } from "react-i18next"

import { FormControl } from "@atoms"

import { useEmailRegistrationForm } from "./use-form"
import { ReactComponent as RefreshIcon } from "assets/icons/refresh.svg"
import laparoliereLogo from "assets/laparoliere-logo.png"
import styles from "../styles.module.scss"
import { Button, Card, Form, Input, Radio, Result, Space } from "antd"
import { Controller } from "react-hook-form"
import { UserIcon } from "pages/auth/__components/icons"
import { useRegisterMemberEmail } from "./queries"

const EmailRegistration = (): React.ReactElement => {
  const { t } = useTranslation()
  const registerMemberEmail = useRegisterMemberEmail()
  const {
    handleSubmit,
    setError,
    control,
    watch,
    formState: { errors },
  } = useEmailRegistrationForm()

  const onSubmit = handleSubmit((data) => {
    registerMemberEmail.mutate(data, {
      onError(error) {
        if (error.response?.data?.code === "form/validation") {
          for (const key in error.response.data.errors) {
            setError(key as any, {
              message: error.response.data.errors[key][0].message,
            })
          }
        }
      },
    })
  })

  if (
    registerMemberEmail.isError &&
    registerMemberEmail.error.response?.data?.code !== "form/validation"
  ) {
    return <Error />
  }

  if (registerMemberEmail.isSuccess) {
    return <Success email={watch("email")} />
  }

  return (
    <Card
      style={{
        overflow: "hidden",
        maxWidth: "70ch",
      }}
    >
      <header>
        <img src={laparoliereLogo} alt="laparoliere" />
        <h1 className={`headline-heavy ${styles.banner}`}>
          {t("external.members.register")}
        </h1>
      </header>
      <p>{t("external.members.email_msg_1")}</p>
      <p style={{ marginTop: "var(--size-2)" }}>
        {t("external.members.email_msg_2")}
      </p>

      <Form
        layout="vertical"
        onFinish={onSubmit}
        style={{ marginTop: "var(--size-8)" }}
      >
        <FormControl>
          <Form.Item
            validateStatus={errors.type && "error"}
            help={errors.type?.message}
            label={t("global.type_X", {
              entity: t("global.registration"),
              count: 0,
            })}
          >
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Radio.Group
                  className={styles.verticalRadioGroup}
                  {...field}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  options={[
                    {
                      label: t("external.members.member_option"),
                      value: "member",
                    },
                    {
                      label: t("external.members.non_member_option"),
                      value: "non_member",
                    },
                  ]}
                />
              )}
            />
          </Form.Item>
        </FormControl>
        <FormControl>
          <Form.Item
            validateStatus={errors.email && "error"}
            help={errors.email?.message}
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder={t("auth.email")}
                  size="large"
                  prefix={<UserIcon />}
                  status={errors.email && "error"}
                  {...field}
                />
              )}
            />
          </Form.Item>
        </FormControl>
        <Button
          htmlType="submit"
          block
          type="primary"
          loading={registerMemberEmail.isLoading}
          size="large"
        >
          {t("auth.signUp")}
        </Button>
      </Form>
    </Card>
  )
}

function Success({ email = "Email@email.com" }: { email: string }) {
  const { t } = useTranslation()
  return (
    <Card
      style={{
        overflow: "hidden",
        maxWidth: "70ch",
      }}
    >
      <header>
        <img src={laparoliereLogo} alt="laparoliere" />
        <h1 className={`headline-heavy ${styles.banner}`}>
          {t("external.members.register")}
        </h1>
      </header>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "24px 0",
        }}
      >
        <svg
          viewBox="64 64 896 896"
          focusable="false"
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <path
            d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"
            fill="var(--color-success)"
          />
          <path
            d="M512 140c-205.4 0-372 166.6-372 372s166.6 372 372 372 372-166.6 372-372-166.6-372-372-372zm193.4 225.7l-210.6 292a31.8 31.8 0 01-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.3 0 19.9 5 25.9 13.3l71.2 98.8 157.2-218c6-8.4 15.7-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.4 12.7z"
            fill="white"
          />
          <path
            d="M699 353h-46.9c-10.2 0-19.9 4.9-25.9 13.3L469 584.3l-71.2-98.8c-6-8.3-15.6-13.3-25.9-13.3H325c-6.5 0-10.3 7.4-6.5 12.7l124.6 172.8a31.8 31.8 0 0051.7 0l210.6-292c3.9-5.3.1-12.7-6.4-12.7z"
            fill="var(--color-success)"
          />
        </svg>
      </div>
      <h2 className="header-heavy" style={{ textAlign: "center" }}>
        {t("external.members.email_saved")}
      </h2>
      <p style={{ marginTop: "var(--size-5)" }}>
        {t("external.members.email_registration_msg_1")}
      </p>
      <p style={{ marginTop: "var(--size-2)" }}>
        {t("external.members.email_registration_msg_2")}
      </p>
      <p style={{ marginTop: "var(--size-2)" }}>
        {t("external.members.email_registration_msg_3", { email })}
      </p>
      <p style={{ marginTop: "var(--size-2)" }}>
        {t("external.members.email_registration_msg_4")}
      </p>
      <p style={{ marginTop: "var(--size-5)" }}>
        <strong>{t("global.phone")} :</strong>{" "}
        {t("client_specific.support_phone")}
        <br />
        <strong>{t("auth.email")} :</strong>{" "}
        {t("client_specific.support_email")}
      </p>
    </Card>
  )
}

function Error() {
  const { t } = useTranslation()
  const handleTryAgain = () => window.location.reload()
  return (
    <Result
      status="error"
      title={t("errors.error")}
      subTitle={t("external.members.email_registration_error_msg", {
        lettersCase: "none",
      })}
      extra={[
        <Button type="primary" onClick={handleTryAgain} size="large">
          <Space align="center">
            <RefreshIcon />
            {t("errors.unexpected_error.try_again")}
          </Space>
        </Button>,
      ]}
    />
  )
}

export default EmailRegistration
